import type React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '../../contexts/AuthContext'
import { theme } from '../../theme/theme'

const FormContainer = styled.div`
  max-width: 400px;
  min-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding-bottom: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.textMuted};
`

const LogoText = styled.h1`
  font-size: ${theme.typography.xl};
  font-family: 'Inter', sans-serif;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  text-align: center;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: contain;
  margin: 0 auto;
`

const FormTitle = styled.h1`
  font-size: ${theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const FormLabel = styled.label`
  font-size: ${theme.typography.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const FormInput = styled.input`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const SubmitButton = styled.button<{ $loading?: boolean }>`
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: #000000;
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: ${(props) => (props.$loading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.default};
  margin-top: ${theme.spacing.sm};
  
  &:hover {
    background-color: ${({ theme, $loading }) => ($loading ? theme.colors.primary : theme.colors.primaryDark)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorMessage = styled.div`
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid #ff6b6b;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  color: #ff6b6b;
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const SuccessMessage = styled.div`
  background-color: rgba(151, 251, 87, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${theme.typography.md};
  margin-top: ${theme.spacing.sm};
`

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  text-decoration: underline;
  cursor: pointer;
  font-size: ${theme.typography.md};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-top: ${theme.spacing.md};
`

interface LoginFormProps {
  onToggleMode: () => void
  isSignUp: boolean
  role?: 'coach' | 'athlete'
}

export const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, isSignUp, role }) => {
  const { signIn, signUp, resetPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showResetPassword, setShowResetPassword] = useState(false)

  const signupText = role === 'athlete' ? 'Sign Up as Athlete' : 'Sign Up as Coach'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password, fullName, role)
        if (error) {
          setError(error.message)
        } else {
          setSuccess('Your account as created successfully! Please check your email for verification.')
          // navigate to dashboard, 1s delay to show the success message
          // and make sure the role is persisted
          setTimeout(() => {
            navigate('/')
          }, 1000) // 1 second delay
        }
      } else {
        const { error } = await signIn(email, password)
        if (error) {
          setError(error.message)
        }
      }
    } catch (_err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('Please enter your email address')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const { error } = await resetPassword(email)
      if (error) {
        setError(error.message)
      } else {
        setSuccess('Password reset email sent! Check your inbox.')
        setShowResetPassword(false)
      }
    } catch (_err) {
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (showResetPassword) {
    return (
      <FormContainer>
        <LogoContainer>
          <Logo src="/logo_512.png" alt="supacoach Logo" />
          <LogoText>supacoach</LogoText>
        </LogoContainer>
        <FormTitle>Reset Password</FormTitle>
        <Form onSubmit={handleResetPassword}>
          <FormField>
            <FormLabel>Email</FormLabel>
            <FormInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </FormField>
          <SubmitButton type="submit" $loading={loading} disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Email'}
          </SubmitButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          <FormActions>
            <LinkButton type="button" onClick={() => setShowResetPassword(false)}>
              Back to login
            </LinkButton>
          </FormActions>
        </Form>
      </FormContainer>
    )
  }

  return (
    <FormContainer>
      <LogoContainer>
        <Logo src="/logo_512.png" alt="supacoach Logo" />
        <LogoText>supacoach</LogoText>
      </LogoContainer>
      <FormTitle>{isSignUp ? signupText : 'Sign In'}</FormTitle>
      <Form onSubmit={handleSubmit}>
        {isSignUp && (
          <FormField>
            <FormLabel>Full Name</FormLabel>
            <FormInput
              type="text"
              value={fullName}
              disabled={success !== null}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </FormField>
        )}

        <FormField>
          <FormLabel>Email</FormLabel>
          <FormInput
            type="email"
            value={email}
            disabled={success !== null}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
          />
        </FormField>

        <FormField>
          <FormLabel>Password</FormLabel>
          <FormInput
            type="password"
            value={password}
            disabled={success !== null}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
            required
            minLength={6}
          />
        </FormField>

        <SubmitButton type="submit" $loading={loading} disabled={loading || success !== null}>
          {loading ? (isSignUp ? 'Creating...' : 'Signing in...') : isSignUp ? 'Create Account' : 'Sign In'}
        </SubmitButton>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <FormActions>
          <LinkButton type="button" onClick={onToggleMode}>
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </LinkButton>

          {isSignUp && <LinkButton onClick={() => navigate('/login')}>Back to Roles</LinkButton>}

          {!isSignUp && (
            <LinkButton type="button" onClick={() => setShowResetPassword(true)}>
              Forgot your password?
            </LinkButton>
          )}
        </FormActions>
      </Form>
    </FormContainer>
  )
}
